// Shared app-update helpers (Alex 2026-06-29: a manual "update available" button in
// Settings, so you're never stuck on an old cached build). Same detection the
// UpdatePrompt toast uses: compare the running build to the deployed <meta name="build">,
// and force a clean reload (drop the SW + its precache) so the new bundle actually loads.

export const MY_VERSION = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev';

// The deployed build id, read from the LIVE index.html (cache-busted, no-store) so it
// bypasses the HTTP + service-worker caches and reflects what's actually deployed.
export async function fetchServerBuild(): Promise<string | null> {
  try {
    const html = await fetch(`/index.html?_=${Date.now()}`, { cache: 'no-store' }).then((r) => r.text());
    const v = html.match(/<meta\s+name=["']build["']\s+content=["']([^"']+)["']/i);
    return v ? v[1] : null;
  } catch {
    return null;
  }
}

// Force the new bundle to load: unregister the service worker + delete its caches,
// then reload. Without this, the SW re-serves the old precached bundle and the
// version never changes (the toast-loop Alex hit before).
export async function forceUpdateReload(): Promise<void> {
  // WATCHDOG (Alex 2026-07-03 "Lädt… hängt, does not update"): on iOS Safari the
  // SW-unregister / caches.delete promises can hang FOREVER in some states — the
  // await then blocked the navigation below and the button spun endlessly. The
  // cleanup is best-effort anyway (the cache-busted navigation is what really
  // fetches the new build), so cap it at 2.5s and navigate NO MATTER WHAT.
  const cleanup = (async () => {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if (typeof caches !== 'undefined') {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch { /* best effort — reload anyway */ }
  })();
  await Promise.race([cleanup, new Promise((r) => setTimeout(r, 2500))]);
  // Leave a breadcrumb so the post-reload app can confirm "✓ updated to vX" — without
  // it the user couldn't tell the update actually applied (#141).
  try { localStorage.setItem('ark-just-updated', '1'); } catch { /* ignore */ }
  // A plain location.reload() can re-serve the iOS HTTP cache / a lingering SW precache,
  // so the version never changes and the button "does nothing" (Alex stuck on 0.457). A
  // cache-busted navigation forces a fresh index.html fetch → the new hashed assets.
  try {
    const u = new URL(window.location.href);
    u.searchParams.set('_v', String(Date.now()));
    window.location.replace(u.toString());
  } catch {
    window.location.reload();
  }
}
